import { useDisclosure } from "@chakra-ui/hooks";
import AtriumApi, { getApiErrorMessage } from "api";
import Badge from "components/badge";
import Button, { IconButton } from "components/button";
import DataTable from "components/data-table";
import Dialog from "components/dialog";
import { useConfirm } from "components/dialog/ConfirmProvider";
import { InputField, Select, Textarea } from "components/fields";
import Skeleton from "components/skeleton/Skeleton";
import { EmptyState, ErrorState } from "components/states";
import { statusTone } from "lib/statusTone";
import notify from "lib/notify";
import { useEffect, useState } from "react";
import { MdAdd, MdDeleteOutline, MdEdit } from "react-icons/md";
import { track } from "lib/mixpanel";
import { EVENTS } from "lib/events";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { EMPTY } from "utils/format";
import { useAuth } from "hooks/useAuth";
import SettingsSection, { SettingsFormActions } from "./components/SettingsSection";

const gradesObject = {
  0: "N/A",
  1: "Preschool",
  2: "Pre-kindergarten",
  3: "Kindergarten",
  4: "1st Grade",
  5: "2nd Grade",
  6: "3rd Grade",
  7: "4th Grade",
  8: "5th Grade",
  9: "6th Grade",
  10: "7th Grade",
  11: "8th Grade",
};

const GRADE_OPTIONS = Object.entries(gradesObject).map(([value, label]) => ({ value, label }));

const DAYS_OF_WEEK = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const DAY_OPTIONS = DAYS_OF_WEEK.map((label, value) => ({ value: String(value), label }));

const SERVICE_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const TIMEZONE_OPTIONS = [
  { value: "America/New_York", label: "Eastern Time (UTC-5 / UTC-4)" },
  { value: "America/Chicago", label: "Central Time (UTC-6 / UTC-5)" },
  { value: "America/Denver", label: "Mountain Time (UTC-7 / UTC-6)" },
  { value: "America/Los_Angeles", label: "Pacific (UTC-8 / UTC-7)" },
  { value: "America/Anchorage", label: "Alaska Time (UTC-9 / UTC-8)" },
  { value: "Pacific/Honolulu", label: "Hawaii-Aleutian Time (UTC-10 / UTC-9)" },
  { value: "America/Phoenix", label: "Arizona (most areas) (UTC-7)" },
];

const EMPTY_WORSHIP_SERVICE = {
  name: "",
  day_of_week: "",
  time: "",
  duration: "",
  location: "",
  campus: "",
  description: "",
  status: "active",
  capacity: "",
};

const churchSchema = Yup.object().shape({
  name: Yup.string().required("Church name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  phone: Yup.string()
    .matches(/^[0-9\s\-()+]*$/, "Phone number should only contain numbers.")
    .required("Phone number is required"),
  address: Yup.string().required("Address is required"),
  city: Yup.string().required("City is required"),
  state: Yup.string().required("State is required"),
  zip: Yup.string().required("Zip code is required"),
});

const roomSchema = Yup.object().shape({
  name: Yup.string().required("Room name is required"),
  capacity: Yup.string()
    .matches(/^[0-9\s\-()+]*$/, "Only numbers are allowed")
    .required("Capacity number is required"),
});

const worshipServiceSchema = Yup.object().shape({
  name: Yup.string().required("Service name is required"),
  day_of_week: Yup.number().required("Day of week is required"),
  time: Yup.string().required("Time is required"),
  duration: Yup.number().min(1, "Duration must be at least 1 minute"),
  capacity: Yup.number().min(1, "Capacity must be at least 1"),
  campus: Yup.string().required("Campus is required"),
  status: Yup.string().required("Status is required"),
});

const RowActions = ({ name, onEdit, onDelete }) => (
  <div className="flex items-center justify-end gap-1">
    <IconButton icon={<MdEdit />} label={`Edit ${name}`} size="sm" onClick={onEdit} />
    <IconButton icon={<MdDeleteOutline />} label={`Delete ${name}`} size="sm" onClick={onDelete} />
  </div>
);

const GeneralSettings = () => {
  const { user } = useAuth();
  const confirm = useConfirm();
  const id = user?.id;

  // Church details state
  const [church, setChurch] = useState({});
  const [loading, setLoading] = useState(true);
  const [churchError, setChurchError] = useState(null);
  const [isSavingChurch, setIsSavingChurch] = useState(false);

  // Rooms state
  const [rooms, setRooms] = useState([]);
  const [room, setRoom] = useState({ name: "", capacity: "", grade: "" });
  const [isSavingRoom, setIsSavingRoom] = useState(false);
  const roomDialog = useDisclosure();

  // Worship services state
  const [worshipServices, setWorshipServices] = useState([]);
  const [worshipService, setWorshipService] = useState(EMPTY_WORSHIP_SERVICE);
  const [worshipServicesLoading, setWorshipServicesLoading] = useState(true);
  const [worshipServicesError, setWorshipServicesError] = useState(null);
  const [isSavingService, setIsSavingService] = useState(false);
  const serviceDialog = useDisclosure();

  // Campuses for worship service dropdown
  const [campuses, setCampuses] = useState([]);

  // Timezone state
  const [timezone, setTimezone] = useState("");
  const [isTimeZoneLoading, setIsTimeZoneLoading] = useState(false);
  const [updateTimeLoading, setUpdateTimeLoading] = useState(false);

  // ==================== Church Details ====================

  const getChurchDetails = async () => {
    setLoading(true);
    setChurchError(null);
    try {
      const resp = await AtriumApi.get("/v1/church", { params: { "with-rooms": true } });
      setChurch(resp?.data?.church);
      setRooms(resp?.data?.rooms);
    } catch (error) {
      setChurchError(error);
    } finally {
      setLoading(false);
    }
  };

  const onSaveChurch = async (values, { setSubmitting }) => {
    setIsSavingChurch(true);
    const {
      lat,
      lng,
      dateFormat,
      timezone: churchTimezone,
      createdAt,
      updatedAt,
      zip,
      ...otherChurchData
    } = church;

    const details = {
      ...otherChurchData,
      ...values,
      zipCode: church?.zipCode || church?.zip,
    };

    try {
      const response = await AtriumApi.put("/v1/church", { church: details });

      if (response?.status === 422) {
        notify.error("Please fix the errors and try again.");
      } else if (response?.status === 200) {
        track(EVENTS.SETTINGS_GENERAL_SAVED);
        notify.success("Organization details saved");
        setChurch(response?.data?.church);
        getChurchDetails();
      } else {
        throw new Error("Unexpected response status: " + response?.status);
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not save organization details"));
    } finally {
      setIsSavingChurch(false);
      setSubmitting(false);
    }
  };

  // ==================== Rooms ====================

  const newRoom = () => {
    setRoom({ name: "", capacity: "", grade: "" });
    roomDialog.onOpen();
  };

  const editRoom = (r) => {
    setRoom(r);
    roomDialog.onOpen();
  };

  const onSaveRoom = async (values) => {
    setIsSavingRoom(true);

    let api, url;
    if (room.id) {
      api = AtriumApi.put;
      url = "/v1/rooms/" + room.id;
    } else {
      api = AtriumApi.post;
      url = "/v1/rooms";
    }

    const payload = room.id ? { room: values } : { ...values };

    try {
      const response = await api(url, { ...payload });

      if (response.status === 422) {
        notify.error(response.data.error || "Validation error");
      } else if (response.status === 200) {
        if (room.id) {
          const collection = [...rooms];
          const index = collection.findIndex((r) => r.id === room.id);
          collection[index] = response.data;
          setRooms(collection);
        } else {
          setRooms([...rooms, response.data]);
        }
        notify.success("Room saved");
        roomDialog.onClose();
      } else {
        throw new Error("Unexpected response");
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not save room"));
    } finally {
      setIsSavingRoom(false);
    }
  };

  const deleteRoom = async (r) => {
    const confirmed = await confirm({
      title: `Delete "${r?.name}"?`,
      message: "This room will be removed from scheduling and check-in. This cannot be undone.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!confirmed) return;
    try {
      await AtriumApi.delete(`/v1/rooms/${r?.id}`);
      notify.success("Room deleted");
      getChurchDetails();
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Could not delete room"));
    }
  };

  // ==================== Worship Services ====================

  const getWorshipServices = async () => {
    setWorshipServicesLoading(true);
    setWorshipServicesError(null);
    try {
      const response = await AtriumApi.get("/v2/worship-services");
      if (response?.data) {
        setWorshipServices(response.data);
      }
    } catch (error) {
      setWorshipServicesError(error);
    } finally {
      setWorshipServicesLoading(false);
    }
  };

  const getCampuses = async () => {
    try {
      const response = await AtriumApi.get("/v2/campuses/allCampuses");
      const campusData = response?.data?.data?.campuses?.data;
      if (campusData && Array.isArray(campusData)) {
        setCampuses(campusData);
      } else {
        setCampuses([]);
      }
    } catch (error) {
      console.error("Failed to load campuses:", error);
      setCampuses([]);
    }
  };

  const newWorshipService = () => {
    setWorshipService(EMPTY_WORSHIP_SERVICE);
    serviceDialog.onOpen();
  };

  const editWorshipService = (service) => {
    setWorshipService(service);
    serviceDialog.onOpen();
  };

  const onSaveWorshipService = async (values) => {
    setIsSavingService(true);
    try {
      let response;
      if (worshipService.id) {
        response = await AtriumApi.put(`/v2/worship-services/${worshipService.id}`, values);
      } else {
        response = await AtriumApi.post("/v2/worship-services", values);
      }

      if (response?.status === 200 || response?.status === 201) {
        notify.success("Worship service saved");
        serviceDialog.onClose();
        getWorshipServices();
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not save worship service"));
    } finally {
      setIsSavingService(false);
    }
  };

  const deleteWorshipService = async (service) => {
    const confirmed = await confirm({
      title: `Delete "${service.name}"?`,
      message: "This worship service will be permanently deleted.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!confirmed) return;
    try {
      await AtriumApi.delete(`/v2/worship-services/${service.id}`);
      notify.success("Worship service deleted");
      getWorshipServices();
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Could not delete worship service"));
    }
  };

  // ==================== Timezone ====================

  const getTimeZone = async () => {
    try {
      setIsTimeZoneLoading(true);
      const response = await AtriumApi.get(`/v1/users/get-profile`);
      if (response) {
        setTimezone(response?.data?.user?.timeZone);
      }
    } catch (error) {
      console.error("Failed to load time zone:", error);
    } finally {
      setIsTimeZoneLoading(false);
    }
  };

  const updateTimeZone = async (tz) => {
    try {
      setUpdateTimeLoading(true);
      await AtriumApi.patch(`/v1/users/update/${id}`, { timeZone: tz });
      notify.success("Time zone saved");
      getTimeZone();
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not update time zone"));
    } finally {
      setUpdateTimeLoading(false);
    }
  };

  // ==================== Effects ====================

  useEffect(() => {
    getChurchDetails();
    getWorshipServices();
    getCampuses();
    getTimeZone();
  }, []);

  // ==================== Tables ====================

  const serviceColumns = [
    {
      accessorKey: "name",
      header: "Service Name",
      cell: ({ getValue }) => <span className="font-semibold text-fg">{getValue()}</span>,
    },
    {
      accessorKey: "day_of_week",
      header: "Day",
      cell: ({ getValue }) => DAYS_OF_WEEK[getValue()] || EMPTY,
    },
    {
      accessorKey: "time",
      header: "Time",
      cell: ({ getValue }) => getValue()?.substring(0, 5) || EMPTY,
    },
    {
      accessorKey: "campus",
      header: "Campus",
      cell: ({ getValue }) => getValue() || <span className="text-fg-muted">Not set</span>,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ getValue }) => (
        <Badge tone={statusTone(getValue())} className="capitalize">
          {getValue()}
        </Badge>
      ),
    },
    {
      id: "actions",
      header: () => <span className="sr-only">Actions</span>,
      meta: { align: "right" },
      cell: ({ row }) => (
        <RowActions
          name={row.original.name}
          onEdit={() => editWorshipService(row.original)}
          onDelete={() => deleteWorshipService(row.original)}
        />
      ),
    },
  ];

  const roomColumns = [
    {
      accessorKey: "name",
      header: "Room Name",
      cell: ({ getValue }) => <span className="font-semibold text-fg">{getValue()}</span>,
    },
    { accessorKey: "capacity", header: "Capacity" },
    {
      accessorKey: "grade",
      header: "Grade",
      cell: ({ getValue }) => {
        const grade = getValue();
        return grade !== undefined && grade !== null ? gradesObject[grade] : EMPTY;
      },
    },
    {
      id: "actions",
      header: () => <span className="sr-only">Actions</span>,
      meta: { align: "right" },
      cell: ({ row }) => (
        <RowActions
          name={row.original.name}
          onEdit={() => editRoom(row.original)}
          onDelete={() => deleteRoom(row.original)}
        />
      ),
    },
  ];

  // ==================== Render ====================

  return (
    <div className="flex flex-col gap-5">
      <SettingsSection title="Organization Details" description="Update your organization's contact details.">
        {churchError ? (
          <ErrorState
            message={getApiErrorMessage(churchError, "Could not load organization details.")}
            onRetry={getChurchDetails}
          />
        ) : loading ? (
          <Skeleton lines={6} />
        ) : (
          <Formik
            enableReinitialize
            initialValues={{
              name: church?.name || "",
              email: church?.email || "",
              phone: church?.phone || "",
              address: church?.address || "",
              city: church?.city || "",
              state: church?.state || "",
              zip: church?.zip || "",
            }}
            validationSchema={churchSchema}
            onSubmit={onSaveChurch}
          >
            {({ errors, touched, values, handleChange, handleBlur, isSubmitting }) => {
              const field = (name) => ({
                name,
                value: values[name],
                error: errors[name],
                touched: touched[name],
                onChange: handleChange,
                onBlur: handleBlur,
              });
              return (
                <Form noValidate>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <InputField
                      label="Church Name"
                      required
                      placeholder="Enter your church name"
                      className="md:col-span-2"
                      {...field("name")}
                    />
                    <InputField
                      label="Email Address"
                      required
                      type="email"
                      placeholder="Enter your email address"
                      {...field("email")}
                    />
                    <InputField
                      label="Contact Phone"
                      required
                      type="tel"
                      placeholder="Enter your contact phone"
                      {...field("phone")}
                    />
                    <InputField
                      label="Address"
                      required
                      placeholder="Enter your address"
                      {...field("address")}
                    />
                    <InputField label="City" required placeholder="Enter your city" {...field("city")} />
                    <InputField label="State" required placeholder="Enter your state" {...field("state")} />
                    <InputField
                      label="Zip Code"
                      required
                      placeholder="Enter your zip code"
                      {...field("zip")}
                    />
                  </div>
                  <SettingsFormActions>
                    <Button type="submit" loading={isSubmitting || isSavingChurch}>
                      {isSavingChurch ? "Saving…" : "Save Changes"}
                    </Button>
                  </SettingsFormActions>
                </Form>
              );
            }}
          </Formik>
        )}
      </SettingsSection>

      <SettingsSection
        title="System Preferences"
        description="Your time zone affects only your account."
      >
        {isTimeZoneLoading ? (
          <Skeleton className="h-10 w-full md:max-w-md" />
        ) : (
          <div className="md:max-w-md">
            <Select
              id="time-zone"
              label="Time Zone"
              placeholder="Select a time zone"
              options={TIMEZONE_OPTIONS}
              value={timezone || ""}
              onChange={(e) => setTimezone(e.target.value)}
            />
          </div>
        )}
        <SettingsFormActions>
          <Button
            onClick={() => updateTimeZone(timezone)}
            loading={updateTimeLoading}
            disabled={isTimeZoneLoading}
          >
            {updateTimeLoading ? "Saving…" : "Save Changes"}
          </Button>
        </SettingsFormActions>
      </SettingsSection>

      <SettingsSection
        title="Worship Services"
        description="Add and manage recurring worship services."
        action={
          <Button variant="secondary" leftIcon={<MdAdd />} onClick={newWorshipService}>
            Add Service
          </Button>
        }
      >
        {worshipServicesError ? (
          <ErrorState
            message={getApiErrorMessage(worshipServicesError, "Could not load worship services.")}
            onRetry={getWorshipServices}
          />
        ) : (
          <DataTable
            variant="plain"
            aria-label="Worship services"
            columns={serviceColumns}
            data={worshipServices}
            loading={worshipServicesLoading}
            getRowId={(s) => String(s.id)}
            emptyState={
              <EmptyState
                variant="inline"
                title="No worship services yet. Add one to use it for scheduling and check-in."
              />
            }
          />
        )}
      </SettingsSection>

      <SettingsSection
        title="Rooms"
        description="Add rooms for scheduling and check-in."
        action={
          <Button variant="secondary" leftIcon={<MdAdd />} onClick={newRoom}>
            Add Room
          </Button>
        }
      >
        {churchError ? (
          <ErrorState
            message={getApiErrorMessage(churchError, "Could not load rooms.")}
            onRetry={getChurchDetails}
          />
        ) : (
          <DataTable
            variant="plain"
            aria-label="Rooms"
            columns={roomColumns}
            data={rooms || []}
            loading={loading}
            getRowId={(r) => String(r.id)}
            emptyState={<EmptyState variant="inline" title="No rooms yet." />}
          />
        )}
      </SettingsSection>

      <Dialog
        isOpen={roomDialog.isOpen}
        onClose={roomDialog.onClose}
        title={room.id ? "Edit Room" : "Add Room"}
        footer={
          <>
            <Button variant="secondary" onClick={roomDialog.onClose}>
              Cancel
            </Button>
            <Button type="submit" form="room-form" loading={isSavingRoom}>
              {room.id ? "Save Changes" : "Add Room"}
            </Button>
          </>
        }
      >
        <Formik
          enableReinitialize
          initialValues={room}
          validationSchema={roomSchema}
          onSubmit={onSaveRoom}
        >
          {({ values, errors, touched, handleChange, handleBlur }) => (
            <Form id="room-form" noValidate className="flex flex-col gap-4">
              <InputField
                label="Room Name"
                required
                placeholder="Room name"
                name="name"
                value={values.name}
                error={errors.name}
                touched={touched.name}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              <InputField
                label="Room Capacity"
                required
                placeholder="Room capacity"
                name="capacity"
                inputMode="numeric"
                value={values.capacity}
                error={errors.capacity}
                touched={touched.capacity}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              <Select
                id="grade"
                label="Grade"
                name="grade"
                placeholder="Select grade"
                options={GRADE_OPTIONS}
                value={values.grade ?? ""}
                error={errors.grade}
                touched={touched.grade}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </Form>
          )}
        </Formik>
      </Dialog>

      <Dialog
        isOpen={serviceDialog.isOpen}
        onClose={serviceDialog.onClose}
        size="lg"
        title={worshipService.id ? "Edit Worship Service" : "Add Worship Service"}
        footer={
          <>
            <Button variant="secondary" onClick={serviceDialog.onClose}>
              Cancel
            </Button>
            <Button type="submit" form="worship-service-form" loading={isSavingService}>
              {worshipService.id ? "Save Changes" : "Add Service"}
            </Button>
          </>
        }
      >
        <Formik
          enableReinitialize
          initialValues={worshipService}
          validationSchema={worshipServiceSchema}
          onSubmit={onSaveWorshipService}
        >
          {({ values, errors, touched, handleChange, handleBlur }) => {
            const field = (name) => ({
              name,
              value: values[name] ?? "",
              error: errors[name],
              touched: touched[name],
              onChange: handleChange,
              onBlur: handleBlur,
            });
            return (
              <Form id="worship-service-form" noValidate className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <InputField
                  label="Service Name"
                  required
                  placeholder="e.g. Sunday Morning Service"
                  className="sm:col-span-2"
                  {...field("name")}
                />
                <Select
                  id="day_of_week"
                  label="Day of Week"
                  required
                  placeholder="Select day"
                  options={DAY_OPTIONS}
                  {...field("day_of_week")}
                />
                <InputField label="Time" required type="time" placeholder="09:00" {...field("time")} />
                <InputField label="Duration (Minutes)" type="number" placeholder="90" {...field("duration")} />
                <InputField label="Capacity" type="number" placeholder="200" {...field("capacity")} />
                <Select
                  id="status"
                  label="Status"
                  required
                  options={SERVICE_STATUS_OPTIONS}
                  {...field("status")}
                />
                <Select
                  id="campus"
                  label="Campus"
                  required
                  placeholder="Select campus"
                  options={campuses.map((campus) => ({ value: campus.name, label: campus.name }))}
                  {...field("campus")}
                />
                <InputField
                  label="Location"
                  hint="Optional"
                  placeholder="Main Sanctuary"
                  className="sm:col-span-2"
                  {...field("location")}
                />
                <Textarea
                  id="description"
                  label="Description"
                  hint="Optional"
                  rows={3}
                  placeholder="Brief description of the service…"
                  className="sm:col-span-2"
                  {...field("description")}
                />
              </Form>
            );
          }}
        </Formik>
      </Dialog>
    </div>
  );
};

export default GeneralSettings;
